import { Flex, Spinner } from "@chakra-ui/react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router-dom";
import { BseStateNameDto, useGetBaseDataQuery } from "./autogen/bff-api";
import { Errors } from "./autogen/errors";
import { useAppDispatch, useAppSelector } from "./common/redux/hooks";
import { setBaseData } from "./common/redux/reducers/baseDataReducer";
import { getInitialAuthStateThunk } from "./common/redux/thunks/auth/get-initial-auth-state-thunk";
import { LoginRedirect } from "./common/auth/LoginRedirect";
import { PostLoginRedirect } from "./common/auth/PostLoginRedirect";
import { AssistantProvider } from "./common/agent/AssistantProvider";
import { CustomIntercomProvider } from "./common/support/CustomIntercomProvider/CustomIntercomProvider";
import { ViewAsRoleScope } from "./common/subscription/ViewAsRoleScope";
import { Admin } from "./pages/admin";
import { ContractSearchV2 } from "./pages/contracts/search-v2/ContractSearchV2";
import { EmailVerification } from "./pages/auth/email-confirmed/EmailVerification";
import { Login } from "./pages/auth/login/Login";
import { Register } from "./pages/auth/register/Register";
import { NoOrganizationsFound } from "./pages/auth/no-organizations-found/NoOrganizationsFound";
import { EditBasicBid } from "./pages/bids/edit/EditBasicBid";
import { DeliveredBids } from "./pages/bids/view-all/DeliveredBids";
import { EditContractRoute } from "./pages/contracts/edit/EditContractRoute";
import { ContractSearch } from "./pages/contracts/search/ContractSearch";
import { ContractSettingsRoute } from "./pages/contracts/settings/ContractSettingsRoute";
import { ContractTasks } from "./pages/contracts/view-all-tasks/ContractTasks";
import { ContractDrafts } from "./pages/contracts/view-all/ContractDrafts";
import { Contracts } from "./pages/contracts/view-all/Contracts";
import { PublishedContracts } from "./pages/contracts/view-all/PublishedContracts";
import { ContractViewRoute } from "./pages/contracts/view-single/ContractViewRoute";
import { AcceptEmailInviteWhenLoggedIn } from "./pages/email-invites/accept/AcceptEmailInviteWhenLoggedIn";
import { AcceptEmailInviteWhenLoggedInWithOrgContext } from "./pages/email-invites/accept/AcceptEmailInviteWhenLoggedInWithOrgContext";
import { EmailInvite } from "./pages/email-invites/view-single/EmailInvite";
import { HomeRoute } from "./pages/home/HomeRoute";
import { Notifications } from "./pages/notifications/Notifications";
import { EditOrganizationProfile } from "./pages/organizations/current/edit/EditOrganizationProfile";
import { AccessManagementPage } from "./pages/organizations/current/manage-users/AccessManagementPage";
import { OrganizationsRoute } from "./pages/organizations/search/OrganizationsRoute";
import { ViewOrganizationProfile } from "./pages/organizations/view-single/ViewOrganizationProfile";
import { EditPersonalProfile } from "./pages/personal-profile/edit/EditPersonalProfile";
import { ViewPersonalProfile } from "./pages/personal-profile/view/ViewPersonalProfile";
import { Projects } from "./pages/projects/view-all/Projects";
import { ProjectPage } from "./pages/projects/view-single/ProjectPage";
import { Settings } from "./pages/settings/Settings";
import { EditNegotiationPage } from "./pages/sourcing-events/buyer/edit-negotiation/EditNegotiationPage";
import { EditBseEditRoute } from "./pages/sourcing-events/buyer/edit/EditBseEditRoute";
import { EventsForBuyer } from "./pages/sourcing-events/buyer/view-all/EventsForBuyer";
import { PortalSourcingEventsPage } from "./pages/sourcing-events/portal/view-all/PortalSourcingEventsPage";
import { PortalSourcingEventPage } from "./pages/sourcing-events/portal/view-single/PortalSourcingEventPage";
import { EditBidV2Page } from "./pages/sourcing-events/portal/bid/EditBidV2Page";
import { EditPrivateBidPage } from "./pages/sourcing-events/portal/bid/EditPrivateBidPage";
import { EditParticipationRequestPage } from "./pages/sourcing-events/portal/request/EditParticipationRequestPage";
import { BseViewRoute } from "./pages/sourcing-events/buyer/view-single/BseViewRoute";
import { EventsForSupplier } from "./pages/sourcing-events/supplier/view-all";
import { BseSupplierView } from "./pages/sourcing-events/supplier/view-single/BseSupplierView";
import { AppShellV2 } from "./common/layout/v2/AppShellV2";
import { PageContainer } from "./common/layout/v2/PageContainer";
import { v2Routes } from "./pages/v2/v2Routes";
import { urls } from "./urls";

export const App = () => {
  const authState = useAppSelector((state) => state.authState.state);
  const dispatch = useAppDispatch();
  const { data, isLoading: isLoadingBaseData } = useGetBaseDataQuery();
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getInitialAuthStateThunk());
  }, [dispatch]);

  useEffect(() => {
    if (data) dispatch(setBaseData(data));
  }, [data, dispatch]);

  /**
   * Used for translating API error codes
   * DO NOT REMOVE
   * */
  const types = Errors();

  if (!authState || isLoadingBaseData || (authState.type !== "LoggedOut" && !data)) {
    return (
      <Flex w="100%" h="90vh" alignItems={"center"} justifyContent="center">
        {types}
        <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
      </Flex>
    );
  }
  const getRoutes = (): React.ReactElement => {
    switch (authState.type) {
      case "LoggedOut":
        return (
          <Routes>
            <Route path={urls.emailVerification} element={<EmailVerification />} />
            <Route path={urls.login} element={<Login />} />
            <Route path={urls.register} element={<Register />} />
            <Route path={urls.portal.list} element={<PortalSourcingEventsPage />} />
            <Route path={urls.portal.view.pathname} element={<PortalSourcingEventPage />} />
            <Route path={urls.emailInvite} element={<EmailInvite />} />
            <Route path="/*" element={<LoginRedirect />} />
          </Routes>
        );
      case "LoggedIn": {
        return (
          <Routes>
            <Route path={urls.admin} element={<Admin />} />
            <Route path={urls.organizations.init} element={<NoOrganizationsFound />} />
            <Route path={urls.portal.list} element={<PortalSourcingEventsPage />} />
            <Route path={urls.portal.view.pathname} element={<PortalSourcingEventPage />} />
            <Route path={urls.emailInvite} element={<AcceptEmailInviteWhenLoggedIn />} />
            <Route path="/*" element={<Navigate to={urls.organizations.init} replace />} />
          </Routes>
        );
      }

      case "LoggedInWithOrgContext":
        return (
          <AssistantProvider>
            <Routes>
              <Route path={urls.admin} element={<Admin />} />
              <Route path={urls.home} element={<HomeRoute />} />
              <Route path={urls.portal.list} element={<PortalSourcingEventsPage />} />
              <Route path={urls.portal.view.pathname} element={<PortalSourcingEventPage />} />
              <Route path={urls.portal.bid.edit.paths.intro.pathname} element={<EditBidV2Page />} />
              <Route path={urls.portal.privateBid.edit.pathname} element={<EditPrivateBidPage />} />
              <Route path={urls.portal.request.edit.paths.intro.pathname} element={<EditParticipationRequestPage />} />

              <Route path={urls.events.view.pathname} element={<BseViewRoute />} />
              <Route path={urls.emailInvite} element={<AcceptEmailInviteWhenLoggedInWithOrgContext />} />
              <Route path={urls.events.supplierView.pathname} element={<BseSupplierView />} />
              <Route path={urls.events.edit.paths.intro.pathname} element={<EditBseEditRoute />} />
              <Route path={urls.events.editNegotiation.paths.intro.pathname} element={<EditNegotiationPage />} />

              <Route path={urls.emailInvite} element={<AcceptEmailInviteWhenLoggedIn />} />

              <Route path={urls.events.viewAll} element={<EventsForBuyer />} />

              <Route
                path={urls.events.viewInvitations}
                element={
                  <EventsForSupplier
                    title={t("Bid invitations")}
                    subtitle={t("Events you are invited to deliver a bid for")}
                    statuses={[BseStateNameDto.Published, BseStateNameDto.Completed]}
                  />
                }
              />

              <Route path={urls.bids.viewAll} element={<DeliveredBids />} />

              <Route path={urls.events.viewAll} element={<EventsForBuyer />} />

              <Route path={urls.bids.edit.paths.details.pathname} element={<EditBasicBid />} />

              <Route path={urls.contracts.edit.paths.details.pathname} element={<EditContractRoute />} />

              <Route path={urls.contracts.viewAll} element={<Contracts />} />

              <Route path={urls.contracts.viewDrafts} element={<ContractDrafts />} />

              <Route path={urls.contracts.viewPublished} element={<PublishedContracts />} />

              <Route path={urls.contracts.search} element={<ContractSearch />} />
              <Route path={urls.contracts.searchV2} element={<ContractSearchV2 />} />

              <Route path={urls.contracts.viewTasks} element={<ContractTasks />} />

              <Route path={urls.contracts.settings} element={<ContractSettingsRoute />} />

              <Route path={urls.contracts.viewSingle.pathname} element={<ContractViewRoute />} />

              <Route path={urls.organizations.search} element={<OrganizationsRoute />} />

              <Route path={`${urls.organizations.profile.pathname}/*`} element={<ViewOrganizationProfile />} />

              <Route path={urls.myProfile.organization.edit} element={<EditOrganizationProfile />} />

              <Route path={urls.myProfile.organization.manageUsers} element={<AccessManagementPage />} />

              <Route path={urls.notifications} element={<Notifications />} />

              <Route path={urls.myProfile.personal.view} element={<ViewPersonalProfile />} />
              <Route path={urls.myProfile.personal.edit} element={<EditPersonalProfile />} />

              <Route path={urls.projects.viewAll} element={<Projects />} />
              <Route path={urls.projects.view.pathname} element={<ProjectPage />} />

              <Route path={urls.myProfile.organization.settings} element={<Settings />} />

              <Route element={<AppShellV2 />}>
                {v2Routes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <PageContainer fullWidth={route.fullWidth} fillHeight={route.fillHeight}>
                        {route.element}
                      </PageContainer>
                    }
                  />
                ))}
              </Route>

              <Route path="/*" element={<PostLoginRedirect />} />
            </Routes>
          </AssistantProvider>
        );
    }
  };

  return (
    <CustomIntercomProvider>
      {getRoutes()}
      <ViewAsRoleScope />
    </CustomIntercomProvider>
  );
};
